/**
 * SepLegTimeline.jsx
 * 
 * Exemple où l'on récupère la "signification" (avec address, pays, etc.) du divorce guide (step4)
 * et on l'intègre ici en phase judiciaire.
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';

// Durées par défaut
const DEFAULT_DOCS_MIN = 0.5;
const DEFAULT_DOCS_MAX = 1.0;
const DEFAULT_REDACTION = 1.0;

// Exemple de liste de pays (tirée du divorce guide, signataires / non signataires)
const hagueCountries = [
  { code: "AL", name: "Albanie" },
  { code: "DE", name: "Allemagne" },
  { code: "AD", name: "Andorre" },
  { code: "AG", name: "Antigua-et-Barbuda" },
  { code: "AR", name: "Argentine" },
  { code: "AM", name: "Arménie" },
  { code: "AU", name: "Australie" },
  { code: "AT", name: "Autriche" },
  { code: "AZ", name: "Azerbaïdjan" },
  { code: "BS", name: "Bahamas" },
  { code: "BB", name: "Barbade" },
  { code: "BY", name: "Bélarus" },
  { code: "BE", name: "Belgique" },
  { code: "BZ", name: "Belize" },
  { code: "BA", name: "Bosnie-Herzégovine" },
  { code: "BW", name: "Botswana" },
  { code: "BR", name: "Brésil" },
  { code: "BG", name: "Bulgarie" },
  { code: "CA", name: "Canada" },
  { code: "CL", name: "Chili" },
  { code: "CN", name: "Chine" }, // Note: Applies to Hong Kong SAR and Macao SAR as well
  { code: "CY", name: "Chypre" },
  { code: "CO", name: "Colombie" },
  { code: "CR", name: "Costa Rica" },
  { code: "HR", name: "Croatie" },
  { code: "DK", name: "Danemark" },
  { code: "DO", name: "République dominicaine" },
  { code: "EG", name: "Égypte" },
  { code: "SV", name: "El Salvador" },
  { code: "EE", name: "Estonie" },
  { code: "US", name: "États-Unis d'Amérique" },
  { code: "FI", name: "Finlande" },
  { code: "FR", name: "France" },
  { code: "GE", name: "Géorgie" },
  { code: "GR", name: "Grèce" },
  { code: "HU", name: "Hongrie" },
  { code: "IN", name: "Inde" },
  { code: "IE", name: "Irlande" },
  { code: "IS", name: "Islande" },
  { code: "IL", name: "Israël" },
  { code: "IT", name: "Italie" },
  { code: "JP", name: "Japon" },
  { code: "KZ", name: "Kazakhstan" },
  { code: "KW", name: "Koweït" },
  { code: "LV", name: "Lettonie" },
  { code: "LT", name: "Lituanie" },
  { code: "LU", name: "Luxembourg" },
  { code: "MW", name: "Malawi" },
  { code: "MT", name: "Malte" },
  { code: "MH", name: "Îles Marshall" },
  { code: "MX", name: "Mexique" },
  { code: "MC", name: "Monaco" },
  { code: "ME", name: "Monténégro" },
  { code: "MA", name: "Maroc" },
  { code: "NL", name: "Pays-Bas" },
  { code: "NI", name: "Nicaragua" },
  { code: "MK", name: "Macédoine du Nord" },
  { code: "NO", name: "Norvège" },
  { code: "PK", name: "Pakistan" },
  { code: "PY", name: "Paraguay" },
  { code: "PH", name: "Philippines" },
  { code: "PL", name: "Pologne" },
  { code: "PT", name: "Portugal" },
  { code: "KR", name: "République de Corée" },
  { code: "MD", name: "République de Moldova" },
  { code: "RO", name: "Roumanie" },
  { code: "GB", name: "Royaume-Uni" },
  { code: "RU", name: "Fédération de Russie" },
  { code: "VC", name: "Saint-Vincent-et-les Grenadines" },
  { code: "SM", name: "Saint-Marin" },
  { code: "RS", name: "Serbie" },
  { code: "SC", name: "Seychelles" },
  { code: "SG", name: "Singapour" },
  { code: "SK", name: "Slovaquie" },
  { code: "SI", name: "Slovénie" },
  { code: "ES", name: "Espagne" },
  { code: "LK", name: "Sri Lanka" },
  { code: "SE", name: "Suède" },
  { code: "CH", name: "Suisse" },
  { code: "TN", name: "Tunisie" },
  { code: "TR", name: "Türkiye" },
  { code: "UA", name: "Ukraine" },
  { code: "VE", name: "Venezuela" },
  { code: "VN", name: "Viet Nam" },
].map((c) => ({ ...c, isSignatory: true }));

const nonHagueCountries = [
  { code: "DZ", name: "Algérie" },
].map((c) => ({ ...c, isSignatory: false }));

const allCountries = [...hagueCountries, ...nonHagueCountries].sort((a, b) => a.name.localeCompare(b.name));

// parseMonths pour "1", "2-3", etc.
function parseMonths(str) {
  if (!str) return 0;
  const rangeMatch = str.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    const [_, minS, maxS] = rangeMatch;
    return (parseFloat(minS) + parseFloat(maxS)) / 2;
  }
  const singleMatch = str.match(/(\d+(?:\.\d+)?)/);
  return singleMatch ? parseFloat(singleMatch[1]) : 0;
}

// ======================================================================
// 1) PHASES
// ======================================================================
/**
 * On va scinder en 3 phases : 
 * - preparation
 * - judiciaire
 * - postSeparation
 */

const preparationSteps = [
  {
    id: "intro",
    phase: "preparation",
    title: "Introduction & contexte",
    minDuration: 0,
    maxDuration: 0,
    shortContent: "Aperçu du processus de séparation légale.",
    detailedContent: `
      Cette frise interactive couvre la préparation, 
      le judiciaire (si contestation) et les démarches post-séparation.
    `,
    questions: [],
  },
  {
    id: "cadre_legal",
    phase: "preparation",
    title: "Cadre légal - conjoints de fait ou mariés ?",
    minDuration: 0.25,
    maxDuration: 0.5,
    shortContent: "Clarifiez votre situation légale.",
    detailedContent: `
      Selon votre statut (marié, conjoint de fait), certaines règles 
      s'appliquent à la séparation (pension, patrimoine, etc.).
    `,
    questions: [
      {
        id: "q_cadre_compris",
        label: "Saviez-vous que seuls les mariés profitent du patrimoine familial ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
      },
    ],
  },
  {
    id: "documents",
    phase: "preparation",
    title: "Rassembler les documents",
    shortContent: "Acte de mariage, preuves de revenus, etc.",
    detailedContent: `
      Réunissez les documents nécessaires: acte de mariage, bulletins de paie, avis de cotisation, 
      relevés bancaires. Estimez le temps requis selon votre situation.
    `,
    minDuration: DEFAULT_DOCS_MIN,
    maxDuration: DEFAULT_DOCS_MAX,
    questions: [
      {
        id: "docs_delay",
        label: "Temps prévu (ex: '1' ou '2-3')",
        type: "text",
        help: "Laissez vide pour le délai par défaut (0.5 à 1 mois).",
      },
    ],
  },
  {
    id: "mediation_familiale",
    phase: "preparation",
    title: "Médiation",
    minDuration: 1,
    maxDuration: 2,
    shortContent: "Obligatoire ou non, selon le tribunal.",
    detailedContent: `
      Tentez une médiation familiale pour réduire les coûts et trouver une entente. 
      Les premières heures sont subventionnées, vérifiez votre admissibilité.
    `,
    questions: [
      {
        id: "q_mediation",
        label: "Avez-vous déjà fait de la médiation ?",
        options: [
          { value: "oui", label: "Oui, c'est en cours ou terminé" },
          { value: "non", label: "Non" },
        ],
      },
    ],
  },
  {
    id: "district_judiciaire",
    phase: "preparation",
    title: "District judiciaire",
    shortContent: "Montreal peut être plus lent, mais n'ajoute pas de délai ici.",
    detailedContent: `
      Choisissez le tribunal compétent (Montréal ou autre). 
      Montréal traite un volume de dossiers plus grand, ce qui peut allonger l'attente.
    `,
    minDuration: 0,
    maxDuration: 0,
    questions: [
      {
        id: "q_district",
        label: "Quel est votre district judiciaire ?",
        options: [
          { value: "montreal", label: "Montréal (chargé)" },
          { value: "autre", label: "Autre district" },
        ],
      },
    ],
  },
  {
    id: "step_prepa_docs",
    phase: "preparation",
    title: "Préparation du dossier",
    minDuration: DEFAULT_DOCS_MIN,
    maxDuration: DEFAULT_DOCS_MAX,
    shortContent:
      "Rassemblez l'acte de mariage, documents financiers, etc.",
    detailedContent: `
      Avant de déposer la demande, vous devez réunir les pièces nécessaires : acte de mariage, 
      preuves de revenus, relevés bancaires, etc.
    `,
    questions: [
      {
        id: "docs_delay",
        label: "Temps pour rassembler les documents (ex: '1' ou '2-3')",
        type: "text",
        help: "Laissez vide pour 0.5 à 1 mois par défaut.",
      },
    ],
  },
  {
    id: "step_depot",
    phase: "preparation",
    title: "Déposer la demande de séparation de corps",
    minDuration: 0,
    maxDuration: 0,
    shortContent: "Déposez la requête officielle auprès du tribunal.",
    detailedContent: `
      Remplissez la demande (pension, partage, etc.) et payez les frais. 
      Cette étape enclenche la procédure formelle de séparation de corps.
    `,
    questions: [],
  },
];

// Importation de la "signification" depuis le code du Divorce (step4)
const judiciaireSteps = [
  {
    id: "step_signification",
    phase: "judiciaire",
    title: "Signification de la demande",
    minDuration: 0.25,
    maxDuration: 6,
    shortContent: "Signifiez la demande selon la localisation du conjoint.",
    detailedContent: `
      La procédure de signification dépend de l'adresse (connue ou non), 
      et du lieu de résidence du conjoint (Québec, Canada, étranger).
      En cas d'adresse inconnue, plusieurs modes spéciaux sont possibles.
    `,
    questions: [
      {
        id: "adresse_exacte",
        label: "Connaissez-vous l'adresse exacte de l'autre partie ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
        help: "Cette info détermine la procédure (huissier, publication, etc.).",
      },
      {
        id: "contacts_recents",
        label: "Possibilité d'obtenir l'adresse via famille, employeur, proches ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
        showIf: (answers) => answers["step_signification"]?.adresse_exacte === "non",
        help: "Tentez d'obtenir l'adresse avant d'envisager d'autres options.",
      },
      {
        id: "localisation",
        label: "Où réside l'autre partie ?",
        options: [
          { value: "quebec", label: "Au Québec (1-2 semaines via huissier)" },
          { value: "canada", label: "Ailleurs au Canada (1-3 semaines)" },
          { value: "etranger", label: "À l'étranger" },
        ],
        showIf: (answers) =>
          answers["step_signification"]?.adresse_exacte === "oui" ||
          answers["step_signification"]?.contacts_recents === "oui",
        help: "Les délais varient selon la province ou le pays.",
      },
      {
        id: "pays_etranger",
        label: "Pays (si étranger)",
        type: "select",
        options: allCountries.map((c) => ({
          value: c.code,
          label: `${c.name} ${c.isSignatory ? "(Sign.)" : "(Non sign.)"}`,
        })),
        showIf: (answers) =>
          (answers["step_signification"]?.adresse_exacte === "oui" ||
            answers["step_signification"]?.contacts_recents === "oui") &&
          answers["step_signification"]?.localisation === "etranger",
        help: "Pays signataires = procédure simplifiée.",
      },
      {
        id: "contacts_electroniques",
        label: "Avez-vous des contacts (moins de 3 mois) par e-mail, réseaux sociaux ?",
        options: [
          { value: "oui", label: "Oui - Mode spécial possible" },
          { value: "non", label: "Non" },
        ],
        showIf: (answers) =>
          answers["step_signification"]?.adresse_exacte === "non" &&
          answers["step_signification"]?.contacts_recents === "non",
        help: "Le juge doit autoriser la signification électronique.",
      },
      {
        id: "residence_canada",
        label: "La personne a-t-elle déjà résidé au Canada ?",
        options: [
          { value: "oui", label: "Oui - Publication dans un journal local possible" },
          { value: "non", label: "Non (estimation impossible)" },
        ],
        showIf: (answers) =>
          answers["step_signification"]?.adresse_exacte === "non" &&
          answers["step_signification"]?.contacts_recents === "non" &&
          answers["step_signification"]?.contacts_electroniques === "non",
        help: "Le tribunal peut autoriser la publication si elle résidait autrefois au Canada.",
      },
      {
        id: "mode_special_decision",
        label: "Décision du tribunal sur le mode spécial",
        options: [
          { value: "accepte", label: "Accepté (1-2 semaines après autorisation)" },
          { value: "refuse", label: "Refusé (estimation impossible)" },
        ],
        showIf: (answers) =>
          answers["step_signification"]?.contacts_electroniques === "oui" ||
          answers["step_signification"]?.residence_canada === "oui",
        help: "Le juge peut accepter ou refuser ce mode de signification.",
      },
    ],
  },
  {
    id: "mesures_prov",
    phase: "judiciaire",
    title: "Mesures provisoires",
    shortContent: "Décisions temporaires en attendant la séparation légale définitive.",
    detailedContent: `
      Pour protéger vos intérêts (pension, garde d'enfants, etc.) 
      avant un jugement final, vous pouvez demander des mesures provisoires.
    `,
    minDuration: 0.5,
    maxDuration: 1,
    questions: [
      {
        id: "q_mesures_prov",
        label: "Besoin de mesures provisoires urgentes ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
      },
    ],
  },
  {
    id: "ordonnances_sauvegarde",
    phase: "judiciaire",
    title: "Ordonnances de sauvegarde",
    shortContent: "Urgence absolue ou danger imminent.",
    detailedContent: `
      Si la sécurité ou le bien-être est menacé, 
      un juge peut rendre une ordonnance rapidement (sauvegarde).
    `,
    minDuration: 0.3,
    maxDuration: 0.5,
    showIf: (answers) => answers.mesures_prov?.q_mesures_prov === "oui",
    questions: [],
  },
  {
    // Matching "reponse" step to divorce timeline
    id: "reponse",
    phase: "judiciaire",
    title: "Réponse du conjoint",
    minDuration: 0.5,
    maxDuration: 1,
    shortContent: "Le conjoint peut contester ou accepter la demande.",
    detailedContent: `
      Après la signification, le conjoint peut déposer une défense 
      (contestation) ou accepter la demande, ce qui influence la durée 
      de la procédure.
    `,
    questions: [
      {
        id: "q_reponse_motif",
        label: "Votre conjoint conteste-t-il les motifs de la séparation ?",
        options: [
          { value: "oui", label: "Oui (allonge la procédure)" },
          { value: "non", label: "Non" },
        ],
      },
      {
        id: "q_reponse_partage",
        label: "Conteste-t-il le partage (pension, biens) ?",
        options: [
          { value: "oui", label: "Oui, il conteste" },
          { value: "non", label: "Non, tout est réglé" },
        ],
        showIf: (answers) =>
          answers["reponse"]?.q_reponse_motif === "oui" ||
          answers["reponse"]?.q_reponse_motif === "non",
      },
    ],
  },
  {
    id: "contenu_pension",
    phase: "judiciaire",
    title: "Pension alimentaire",
    minDuration: 1,
    maxDuration: 3,
    shortContent: "Montant fixé selon les barèmes, revenus, et besoins.",
    detailedContent: `
      Le tribunal calcule la pension alimentaire 
      selon les barèmes officiels et la situation financière de chacun.
    `,
    showIf: (answers) => answers.reponse?.reponse_attendue !== undefined,
    questions: [
      {
        id: "q_pension_revenus",
        label: "Possibilité que l'autre partie refuse de divulguer ses revenus ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
      },
    ],
  },
  {
    id: "step_jugement",
    phase: "judiciaire",
    title: "Jugement de séparation de corps",
    minDuration: 1,
    maxDuration: 6,
    shortContent:
      "Le tribunal rend une décision finale (séparation de corps, mesures accessoires).",
    detailedContent: `
      Après la signification et la réponse (ou absence de réponse) du conjoint, 
      le juge rendra un jugement final officialisant la séparation de corps, 
      établissant éventuellement la pension et d'autres mesures.
    `,
    questions: [],
  },
];

const postSteps = [
  {
    id: "faq_checklist",
    phase: "postSeparation",
    title: "FAQ & checklist",
    minDuration: 0,
    maxDuration: 0,
    shortContent: "Foire aux questions, étapes finales.",
    detailedContent: `
      Après le jugement, vérifiez que vous avez bien 
      tous les documents (jugement, copie conforme, etc.).
    `,
    questions: [],
  },
  {
    id: "modifications",
    phase: "postSeparation",
    title: "Modifications ultérieures",
    minDuration: 0,
    maxDuration: 0,
    shortContent: "En cas de changement majeur (revenus, déménagement).",
    detailedContent: `
      Les ordonnances peuvent être révisées 
      si votre situation financière ou familiale évolue.
    `,
    questions: [],
  },
  {
    id: "step_modif",
    phase: "postSeparation",
    title: "Modifications ultérieures",
    minDuration: 0,
    maxDuration: 0,
    shortContent:
      "Si la situation évolue (revenus, déménagement), vous pouvez redemander au tribunal.",
    detailedContent: `
      Les ordonnances rendues lors d'une séparation de corps peuvent être révisées en cas 
      de changement majeur (ex. perte d'emploi, besoin de pension plus élevé, etc.). 
      Vous devrez introduire une nouvelle requête au tribunal.
    `,
    questions: [],
  },
];

// ======================================================================
// 2) FONCTION getAllSteps + PHASES_ORDER
// ======================================================================

function getAllSteps() {
  return [...preparationSteps, ...judiciaireSteps, ...postSteps];
}

const PHASES_ORDER = [
  { id: "preparation", label: "Phase 1 : Préparation" },
  { id: "judiciaire", label: "Phase 2 : Processus judiciaire" },
  { id: "postSeparation", label: "Phase 3 : Suite / Modifications" },
];

// ======================================================================
// 3) CALCUL TIMELINE
// ======================================================================
function calculateTimeline(answers, completedSteps) {
  const phaseHistory = {
    preparation: [],
    judiciaire: [],
    postSeparation: [],
  };

  let totalMin = 0;
  let totalMax = 0;

  const visibleSteps = getAllSteps().filter((st) => {
    if (st.showIf && st.showIf(answers) === false) return false;
    return true;
  });

  function getStepDelay(step) {
    if (!completedSteps.includes(step.id)) {
      return { min: 0, max: 0 };
    }

    // Ex: Personnalisé pour step_prepa_docs
    if (step.id === "step_prepa_docs") {
      const val = parseMonths(answers["step_prepa_docs"]?.docs_delay);
      if (val > 0) {
        phaseHistory[step.phase].push(
          `[Préparation docs] délai personnalisé : ${val} mois`
        );
        return { min: val, max: val };
      } else {
        phaseHistory[step.phase].push(
          `[Préparation docs] délai par défaut : ${DEFAULT_DOCS_MIN}-${DEFAULT_DOCS_MAX} mois`
        );
        return { min: DEFAULT_DOCS_MIN, max: DEFAULT_DOCS_MAX };
      }
    }

    // Ex: signification => si hors Québec
    if (step.id === "step_signification") {
      // On reprend la même logique que le divorce : 
      const s = answers["step_signification"] || {};
      // On simplifie : si "etranger", 1-2 mois signataire, 2-3 mois non signataire
      if (s.adresse_exacte === "oui" || s.contacts_recents === "oui") {
        if (s.localisation === "quebec") {
          phaseHistory.judiciaire.push("[Signification] Québec : ~1 à 2 semaines");
          return { min: 0.25, max: 0.5 }; // 0.25 => ~1 semaine
        }
        if (s.localisation === "canada") {
          phaseHistory.judiciaire.push("[Signification] Canada (hors QC) : ~1-3 semaines");
          return { min: 0.25, max: 0.75 };
        }
        if (s.localisation === "etranger") {
          const c = s.pays_etranger || s.pays_etranger;
          const found = allCountries.find((cc) => cc.code === c);
          if (found?.isSignatory) {
            phaseHistory.judiciaire.push("[Signification] Étranger (sign.) : 1-2 mois");
            return { min: 1, max: 2 };
          } else {
            phaseHistory.judiciaire.push("[Signification] Étranger (non sign.) : 2-3 mois");
            return { min: 2, max: 3 };
          }
        }
      } else {
        // Mode spécial, publication, etc.
        phaseHistory.judiciaire.push(
          "[Signification] Adresse inconnue => mode spécial ou publication"
        );
        // on met 1 à 2 mois par défaut
        return { min: 1, max: 2 };
      }
    }

    // Par défaut
    phaseHistory[step.phase].push(
      `[${step.title}] ${step.minDuration}-${step.maxDuration} mois`
    );
    return { min: step.minDuration, max: step.maxDuration };
  }

  visibleSteps.forEach((st) => {
    const { min, max } = getStepDelay(st);
    totalMin += min;
    totalMax += max;
  });

  return { totalMin, totalMax, phaseHistory };
}

// ======================================================================
// 4) COMPOSANT PRINCIPAL
// ======================================================================
export default function SepLegTimeline() {
  const { t, i18n } = useTranslation('sepleg');
  const [completedSteps, setCompletedSteps] = useState([]);
  const [answers, setAnswers] = useState({});
  const [openStep, setOpenStep] = useState(null);

  const [timelineSummary, setTimelineSummary] = useState({
    totalMin: 0,
    totalMax: 0,
    phaseHistory: {
      preparation: [],
      judiciaire: [],
      postSeparation: [],
    },
  });

  // Recalcul
  useEffect(() => {
    const { totalMin, totalMax, phaseHistory } = calculateTimeline(
      answers,
      completedSteps
    );
    setTimelineSummary({ totalMin, totalMax, phaseHistory });
  }, [answers, completedSteps]);

  // Force component update when language changes
  useEffect(() => {
    // Adding a console log to verify language changes are detected
    console.log("Language changed to:", i18n.language);
    
    // Force re-render by updating a state value
    setTimelineSummary(prev => ({...prev}));
  }, [i18n.language]);

  // handleAnswerChange
  const handleAnswerChange = (stepId, questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [stepId]: {
        ...prev[stepId],
        [questionId]: value,
      },
    }));
  };

  // Vérifie si toutes questions sont remplies
  const areAllQuestionsAnswered = (step) => {
    if (!step.questions || step.questions.length === 0) return true;
    const stepAns = answers[step.id] || {};
    return step.questions.every((q) => {
      if (q.showIf && !q.showIf(answers)) return true;
      if (q.id === "docs_delay") return true; // champ facultatif
      if (q.id === "pays_etranger") {
        // n'affichons que si local. = "etranger"
        if (stepAns.localisation !== "etranger") return true;
      }
      return !!stepAns[q.id];
    });
  };

  // Marquer l'étape comme terminée
  const markStepAsDone = (step) => {
    // verif questions
    if (!areAllQuestionsAnswered(step)) {
      alert(t('seplegGuide.alerts.completeRequired'));
      return;
    }
    // verif étapes précédentes
    const visibleSteps = getAllSteps().filter(
      (s) => !s.showIf || s.showIf(answers) !== false
    );
    const idx = visibleSteps.findIndex((s) => s.id === step.id);
    for (let i = 0; i < idx; i++) {
      if (!completedSteps.includes(visibleSteps[i].id)) {
        alert(t('seplegGuide.alerts.completePrevious'));
        return;
      }
    }
    if (window.confirm(t('seplegGuide.alerts.confirmStep', { step: step.title }))) {
      setCompletedSteps((prev) => [...prev, step.id]);
      setOpenStep(null);
    }
  };

  // Reset
  const resetAll = () => {
    if (window.confirm(t('seplegGuide.actions.resetConfirm'))) {
      setAnswers({});
      setCompletedSteps([]);
      setOpenStep(null);
      setTimelineSummary({
        totalMin: 0,
        totalMax: 0,
        phaseHistory: {
          preparation: [],
          judiciaire: [],
          postSeparation: [],
        },
      });
    }
  };

  // calcul plannedTotal
  const visibleSteps = getAllSteps().filter(
    (st) => !st.showIf || st.showIf(answers) !== false
  );
  const getPlannedStepDelay = (st) => {
    if (st.id === "step_prepa_docs") {
      const val = parseMonths(answers["step_prepa_docs"]?.docs_delay);
      return val > 0 ? val : st.minDuration;
    }
    if (st.id === "step_signification") {
      // ex. 0.25 => 1-2sem ; 0.5 => ~2sem-1mois ; etc.
      // on simplifie : on renvoie st.minDuration
      return st.minDuration;
    }
    return st.minDuration;
  };
  let plannedTotal = 0;
  visibleSteps.forEach((s) => {
    const { minDuration } = s;
    plannedTotal += getPlannedStepDelay(s);
  });

  const { totalMin, totalMax, phaseHistory } = timelineSummary;
  const progressPercent =
    plannedTotal > 0 ? Math.min(Math.round((totalMin / plannedTotal) * 100), 100) : 0;

  // Groupé par phase
  const grouped = PHASES_ORDER.map((ph) => {
    const stepsInPhase = visibleSteps.filter((s) => s.phase === ph.id);
    return { ...ph, steps: stepsInPhase };
  });

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="px-6 py-8 mt-4 bg-blue-50 rounded-xl border border-blue-100 shadow-sm mb-8">
        <h1 className="text-2xl font-bold text-blue-700 mb-3 text-center">
          <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
          {t('sepleg:seplegGuide.timeline.title')}
          <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
        </h1>
        <p className="text-center text-blue-600 max-w-3xl mx-auto">
          {t('sepleg:seplegGuide.timeline.subtitle')}
        </p>
      </div>

      {/* Top 3 cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-8 px-4">
        {/* Card 1: Progression */}
        <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
          <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
            <span className="bg-blue-50 p-2 rounded-full mr-2" role="img" aria-label="chart">📊</span>
            {t('seplegGuide.interface.cards.progress.title')}
          </h3>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="font-semibold">{progressPercent}% {t('seplegGuide.interface.cards.progress.completed')}</span>
              <span className="text-slate-500 text-xs">
                {totalMin.toFixed(1)} / {plannedTotal.toFixed(1)} {t('seplegGuide.interface.cards.progress.months')}
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-3">
              <div
                className="bg-blue-500 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Card 2: Estimation */}
        <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
          <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
            <span className="bg-blue-50 p-2 rounded-full mr-2">⏱️</span>
            {t('seplegGuide.interface.cards.estimation.title')}
          </h3>
          <div className="flex items-center justify-center h-16">
            {totalMin === 0 && totalMax === 0 ? (
              <p className="text-slate-500 text-center text-sm">
                {t('seplegGuide.interface.cards.estimation.completeStepsPrompt')}
              </p>
            ) : (
              <div className="text-center">
                <span className="text-2xl font-bold text-blue-700">
                  {totalMin} {t('seplegGuide.interface.cards.estimation.to')} {totalMax} {t('seplegGuide.interface.cards.estimation.months')}
                </span>
                <p className="text-xs text-slate-500 mt-1">{t('seplegGuide.interface.cards.estimation.basedOnAnswers')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Card 3: Actions */}
        <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
          <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
            <span className="bg-blue-50 p-2 rounded-full mr-2">🔄</span>
            {t('seplegGuide.interface.cards.actions.title')}
          </h3>
          <div className="flex justify-center items-center h-16">
            <button
              onClick={resetAll}
              className="btn btn-outline border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400"
            >
              {t('seplegGuide.interface.cards.actions.reset')}
            </button>
          </div>
        </div>
      </div>

      {/* Phase History */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-8 mx-4">
        <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
          <span className="bg-blue-50 p-2 rounded-full mr-2">📋</span>
          {t('seplegGuide.interface.details.title')}
        </h3>
        {Object.entries(phaseHistory).some(([_, list]) => list.length > 0) ? (
          <div className="space-y-4">
            {Object.entries(phaseHistory).map(([phase, msgs]) => {
              if (!msgs.length) return null;
              return (
                <div key={phase} className={`p-3 rounded-lg ${
                  phase === "preparation"
                    ? "bg-blue-100 border-blue-200 text-blue-700"
                    : phase === "judiciaire"
                    ? "bg-amber-100 border-amber-200 text-amber-700"
                    : "bg-green-100 border-green-200 text-green-700"
                }`}>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <span className="mr-2">
                      {phase === "preparation" ? "📋" : phase === "judiciaire" ? "⚖️" : "📝"}
                    </span>
                    {t(`seplegGuide.phases.${phase === "preparation" ? "preparation" : phase === "judiciaire" ? "judicial" : "postSeparation"}.title`)}
                  </h4>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {msgs.map((m, idx) => (
                      <li key={idx} className="text-slate-700">
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center p-4 text-slate-500">
            <p>{t('seplegGuide.interface.details.noData')}</p>
          </div>
        )}
      </div>

      {/* Steps by phase */}
      <div className="space-y-6 px-4 mb-8">
        {grouped.map((phaseObj) => {
          if (!phaseObj.steps.length) return null;

          return (
            <div key={phaseObj.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Phase header */}
              <div className={`p-4 ${
                phaseObj.id === "preparation"
                  ? "bg-blue-100 text-blue-700"
                  : phaseObj.id === "judiciaire"
                  ? "bg-amber-100 text-amber-700"
                  : "bg-green-100 text-green-700"
              }`}>
                <h2 className="text-lg font-bold flex items-center">
                  <span className="mr-3 text-2xl">
                    {phaseObj.id === "preparation" ? "📋" : phaseObj.id === "judiciaire" ? "⚖️" : "📝"}
                  </span>
                  {phaseObj.label}
                </h2>
              </div>

              {/* Steps inside this phase */}
              <div className="p-4 relative">
                <div className="absolute w-[4px] h-full left-6 top-0 rounded-full z-0 bg-gradient-to-b from-blue-100 via-amber-100 to-green-100"></div>

                <div className="space-y-8">
                  {phaseObj.steps.map((step, idx) => {
                    const globalIndex = getAllSteps().findIndex((x) => x.id === step.id);
                    const isDone = completedSteps.includes(step.id);
                    const isOpen = openStep === step.id;
                    const isActive = isOpen && !isDone;

                    let style = {
                      bg: "bg-white",
                      border: "border-slate-200",
                      text: "text-slate-700",
                      bulletBg: "bg-white",
                      bulletBorder: "border-slate-300",
                      bulletText: "text-slate-700",
                    };

                    if (isDone) {
                      style = {
                        bg: "bg-green-50",
                        border: "border-green-200",
                        text: "text-green-700",
                        bulletBg: "bg-green-500",
                        bulletBorder: "border-green-500",
                        bulletText: "text-white",
                      };
                    } else if (isActive) {
                      style = {
                        bg: "bg-amber-50",
                        border: "border-amber-200",
                        text: "text-amber-700",
                        bulletBg: "bg-amber-50",
                        bulletBorder: "border-amber-500",
                        bulletText: "text-amber-700",
                      };
                    }

                    return (
                      <div key={step.id} className="flex flex-col md:flex-row relative z-10">
                        <div className="mr-6">
                          <div
                            className={`w-12 h-12 rounded-full border-4 shadow-sm flex items-center justify-center text-lg font-bold 
                              ${style.bulletBg} ${style.bulletBorder} ${style.bulletText}
                              transition-all duration-300 ease-in-out
                              ${isDone ? "scale-110" : ""}
                            `}
                            style={{ marginLeft: "-4px" }}
                          >
                            {isDone ? "✓" : globalIndex + 1}
                          </div>
                        </div>

                        <div className="flex-1 pt-1">
                          <div
                            className={`card w-full border ${style.border} ${style.bg} rounded-xl shadow-sm
                            hover:shadow-md transition-all duration-200 ease-in-out
                            ${isActive ? "transform scale-[1.02]" : ""}
                            `}
                          >
                            <div className="card-body p-5">
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                <h3 className={`card-title text-lg ${style.text}`}>
                                  {step.title}
                                </h3>
                              </div>
                              <p className="mt-3 text-slate-600">
                                {step.shortContent}
                              </p>

                              {/* Buttons */}
                              <div className="mt-4 flex gap-3 flex-wrap">
                                <button
                                  className={`btn btn-sm rounded-full px-4 ${
                                    isOpen
                                      ? "bg-amber-100 border-amber-200 text-amber-700 hover:bg-amber-200"
                                      : "bg-blue-100 border-blue-200 text-blue-700 hover:bg-blue-200"
                                  }`}
                                  onClick={() => setOpenStep(isOpen ? null : step.id)}
                                >
                                  {isOpen ? (
                                    <>
                                      Masquer
                                      <span className="ml-1">▲</span>
                                    </>
                                  ) : (
                                    <>
                                      Détails & Questions
                                      <span className="ml-1">▼</span>
                                    </>
                                  )}
                                </button>

                                {!isDone && (
                                  <button
                                    className="btn btn-sm rounded-full px-4 bg-green-100 border-green-200 text-green-700 hover:bg-green-200"
                                    onClick={() => markStepAsDone(step)}
                                  >
                                    ✓ Terminer
                                  </button>
                                )}

                                {isDone && (
                                  <span className="text-green-600 bg-green-50 px-3 py-1 rounded-full text-sm font-semibold border border-green-100 self-center">
                                    Étape terminée
                                  </span>
                                )}
                              </div>

                              {/* Accordion content */}
                              {isOpen && (
                                <div className="mt-5 animate-[fadeIn_0.3s_ease-in-out]">
                                  <style>{`
                                    @keyframes fadeIn {
                                      from { opacity: 0; transform: translateY(-10px); }
                                      to { opacity: 1; transform: translateY(0); }
                                    }
                                  `}</style>

                                  {/* Detailed content */}
                                  <div className="p-4 border border-slate-200 rounded-lg bg-white text-slate-600 mb-4">
                                    <h4 className="font-semibold text-blue-700 mb-2">
                                      Détails de l'étape
                                    </h4>
                                    {typeof step.detailedContent === "string" ? (
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: step.detailedContent,
                                        }}
                                      />
                                    ) : (
                                      step.detailedContent
                                    )}
                                  </div>

                                  {/* Questions */}
                                  {step.questions?.length > 0 && (
                                    <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                                      <h4 className="font-semibold text-amber-700 mb-3 flex items-center">
                                        <span className="mr-2">❓</span>
                                        Questions pour adapter l'estimation
                                      </h4>
                                      <div className="space-y-5">
                                        {step.questions.map((q) => {
                                          if (typeof q.showIf === "function" && !q.showIf(answers)) {
                                            return null;
                                          }
                                          const stepAns = answers[step.id] || {};
                                          const selectedValue = stepAns[q.id] || "";

                                          if (q.type === "text" || q.type === "number") {
                                            return (
                                              <div
                                                key={q.id}
                                                className="bg-white p-3 rounded-lg border border-amber-100"
                                              >
                                                <label className="font-semibold block mb-1 text-blue-700">
                                                  {q.label}
                                                </label>
                                                {q.help && (
                                                  <p className="text-xs text-slate-500 mb-3">
                                                    {q.help}
                                                  </p>
                                                )}
                                                <input
                                                  type={q.type}
                                                  className="input input-bordered w-full max-w-md border-slate-300 focus:border-blue-400 focus:ring-blue-200"
                                                  value={selectedValue}
                                                  onChange={(e) =>
                                                    handleAnswerChange(
                                                      step.id,
                                                      q.id,
                                                      e.target.value
                                                    )
                                                  }
                                                  placeholder={q.type === "text" ? "Votre réponse..." : "0"}
                                                />
                                              </div>
                                            );
                                          }

                                          if (q.type === "select") {
                                            return (
                                              <div
                                                key={q.id}
                                                className="bg-white p-3 rounded-lg border border-amber-100"
                                              >
                                                <label className="font-semibold block mb-1 text-blue-700">
                                                  {q.label}
                                                </label>
                                                {q.help && (
                                                  <p className="text-xs text-slate-500 mb-3">
                                                    {q.help}
                                                  </p>
                                                )}
                                                <select
                                                  className="select select-bordered w-full max-w-md border-slate-300 focus:border-blue-400 focus:ring-blue-200"
                                                  value={selectedValue}
                                                  onChange={(e) =>
                                                    handleAnswerChange(
                                                      step.id,
                                                      q.id,
                                                      e.target.value
                                                    )
                                                  }
                                                >
                                                  <option value="">
                                                    -- Sélectionnez une option --
                                                  </option>
                                                  {q.options?.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                      {opt.label}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                            );
                                          }

                                          if (q.options && !q.type) {
                                            const ops =
                                              typeof q.options === "function"
                                                ? q.options(answers)
                                                : q.options;
                                            return (
                                              <div
                                                key={q.id}
                                                className="bg-white p-3 rounded-lg border border-amber-100"
                                              >
                                                <label className="font-semibold block mb-1 text-blue-700">
                                                  {q.label}
                                                </label>
                                                {q.help && (
                                                  <p className="text-xs text-slate-500 mb-3">
                                                    {q.help}
                                                  </p>
                                                )}
                                                <div className="space-y-2 mt-3">
                                                  {ops.map((opt) => (
                                                    <label
                                                      key={opt.value}
                                                      className={`flex items-center gap-2 p-2 hover:bg-blue-50 rounded-md transition-colors
                                                        ${
                                                          selectedValue === opt.value
                                                            ? "bg-blue-50 border border-blue-100"
                                                            : ""
                                                        }
                                                        ${
                                                          opt.disabled
                                                            ? "opacity-50 cursor-not-allowed"
                                                            : "cursor-pointer"
                                                        }
                                                      `}
                                                    >
                                                      <input
                                                        type="radio"
                                                        className="radio radio-sm radio-primary"
                                                        name={`${step.id}-${q.id}`}
                                                        value={opt.value}
                                                        checked={selectedValue === opt.value}
                                                        onChange={() => {
                                                          if (!opt.disabled) {
                                                            handleAnswerChange(step.id, q.id, opt.value);
                                                          }
                                                        }}
                                                        disabled={opt.disabled}
                                                      />
                                                      <span>{opt.label}</span>
                                                    </label>
                                                  ))}
                                                </div>
                                              </div>
                                            );
                                          }

                                          return null;
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legal note */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600 mb-8 mx-4">
        <p className="italic text-center">
          {t('seplegGuide.legalNote')}
        </p>
      </div>
    </div>
  );
}
